import React, {
  useCallback, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Box, HStack, Text } from '@gluestack-ui/themed';

import { ChartEntranceGate } from '@/admin/dashboards/ChartEntranceGate';
import { DashboardPage } from '@/admin/dashboards/DashboardPage';
import { DashboardSectionEnter } from '@/admin/dashboards/DashboardSectionEnter';
import { useReduceMotionEnabled } from '@/admin/dashboards/useReduceMotionEnabled';
import { FigureBlock } from '@/admin/dashboards/FigureBlock';
import { SparklineTooltipLayerContext } from '@/admin/dashboards/sparklineTooltipLayer';
import { HealthFigureDivider } from '@/admin/dashboards/HealthFigureDivider';
import { HelpfulnessChartLegend } from '@/admin/dashboards/HelpfulnessChartLegend';
import { ReasonList } from '@/admin/dashboards/ReasonList';
import { StackedBarChart } from '@/admin/dashboards/StackedBarChart';
import { TrendChart } from '@/admin/dashboards/TrendChart';
import { TrendChartLegend } from '@/admin/dashboards/TrendChartLegend';
import {
  formatScoreAttemptAxis,
  formatScoreSessionWeekAxis,
  formatChartWeekAxis,
} from '@/admin/dashboards/chartAxisFormat';
import {
  formatAttemptsTooltipValue,
  formatAttemptsValue,
  formatCountValue,
  formatPercentFigureValue,
  formatPercentValue,
  formatScoreGainFigureValue,
  formatScoreGainPp,
  PERCENT_FIGURE_UNIT,
  SCORE_GAIN_FIGURE_UNIT,
} from '@/admin/dashboards/figureDisplay';
import { Sparkline } from '@/admin/dashboards/Sparkline';
import { TutorHealthLegend } from '@/admin/dashboards/TutorHealthLegend';
import { useChartTheme } from '@/admin/dashboards/useChartTheme';
import {
  XAxisToggle,
  type ScoreProgressionAxisMode,
} from '@/admin/dashboards/XAxisToggle';

import { useAiTutorDashboard } from './useAiTutorDashboard';

const SECTION_HAIRLINE = '#EAECF0';

type DashboardSectionProps = {
  testID: string;
  title: string;
  first?: boolean;
  dimmed?: boolean;
  headerRight?: React.ReactNode;
  children: React.ReactNode;
};

function DashboardSection({
  testID,
  title,
  first = false,
  dimmed = false,
  headerRight,
  children,
  sectionIndex,
  reduceMotion,
}: DashboardSectionProps & {
  sectionIndex: number;
  reduceMotion: boolean;
}) {
  return (
    <DashboardSectionEnter reduceMotion={reduceMotion} sectionIndex={sectionIndex}>
      <Box
        testID={testID}
        opacity={dimmed ? 0.55 : 1}
        borderTopWidth={1}
        style={{ borderTopColor: SECTION_HAIRLINE }}
        pt={20}
        mt={first ? 16 : 32}
      >
        <HStack justifyContent="space-between" alignItems="center" flexWrap="wrap" space="sm">
          <Text
            testID={`${testID}-heading`}
            fontFamily="$heading"
            color="$textHeading"
            sx={{ fontSize: 16, lineHeight: 24, fontWeight: '600' }}
          >
            {title}
          </Text>
          {headerRight}
        </HStack>
        {children}
      </Box>
    </DashboardSectionEnter>
  );
}

/**
 * Admin Reports → AI Tutor dashboard (Draft Coach analytics).
 */
const AiTutorDashboard: React.FC = () => {
  const { t } = useTranslation();
  const chartTheme = useChartTheme();
  const reduceMotion = useReduceMotionEnabled();
  const [scoreAxisMode, setScoreAxisMode] = useState<ScoreProgressionAxisMode>('attempt');
  const { dashboard, viewModel } = useAiTutorDashboard();
  const { isFetching, status } = dashboard;
  const chartReplayToken = useMemo(() => {
    const filters = dashboard.appliedFilters;
    return [
      scoreAxisMode,
      filters.gated,
      filters.from,
      filters.to,
      filters.excludePasteHeavy,
      filters.excludeFirstTryPasses,
    ].join('|');
  }, [dashboard.appliedFilters, scoreAxisMode]);
  const chartAnimationActive = !reduceMotion;

  const scoreProgressionPoints = scoreAxisMode === 'attempt'
    ? viewModel?.scoreByAttempt
    : viewModel?.scoreBySessionWeek;

  const formatScoreX = useCallback(
    (x: Parameters<typeof formatScoreAttemptAxis>[0]) => (
      scoreAxisMode === 'attempt'
        ? formatScoreAttemptAxis(x)
        : formatScoreSessionWeekAxis(x)
    ),
    [scoreAxisMode],
  );

  const releaseLines = viewModel?.releaseLines;
  const figuresDimmed = isFetching && status === 'ready';

  const tooltipOpenCount = useRef(0);
  const [healthRaised, setHealthRaised] = useState(false);
  const raiseHealthForTooltip = useCallback((open: boolean) => {
    tooltipOpenCount.current = Math.max(0, tooltipOpenCount.current + (open ? 1 : -1));
    setHealthRaised(tooltipOpenCount.current > 0);
  }, []);

  const voteRateDisplay = viewModel?.voteRate != null
    ? formatPercentFigureValue(viewModel.voteRate)
    : '—';

  const sparklineProps = {
    releaseLines,
    theme: chartTheme,
    chartAnimationActive,
    formatTooltipValue: (v: number | null | undefined) => (
      v == null || Number.isNaN(v) ? '—' : String(v)
    ),
  };

  const vm = viewModel;

  return (
    <DashboardPage dashboard={dashboard}>
      <SparklineTooltipLayerContext.Provider value={raiseHealthForTooltip}>
        <DashboardSection
          testID="tutor-health-section"
          first
          sectionIndex={0}
          reduceMotion={reduceMotion}
          title={t('admin.analytics.tutorHealth.heading')}
          headerRight={<TutorHealthLegend />}
        >
          <Box
            position="relative"
            zIndex={healthRaised ? 20 : undefined}
            style={{ overflow: 'visible', elevation: healthRaised ? 20 : 0 }}
          >
            <HStack alignItems="stretch" flexWrap="wrap">
              <FigureBlock
                testID="attempts-to-done-figure"
                label={t('admin.analytics.aiTutor.attempts.label')}
                value={formatAttemptsValue(vm?.attempts.value ?? null)}
                animateNumeric={vm?.attempts.value ?? null}
                formatAnimated={formatAttemptsValue}
                reduceMotion={reduceMotion}
                staggerIndex={0}
                unit={t('admin.analytics.aiTutor.attempts.unit')}
                caption={t('admin.analytics.aiTutor.attempts.caption')}
                tooFew={!!vm?.attempts.tooFew}
                dimmed={figuresDimmed}
                sparkline={(
                  <Sparkline
                    {...sparklineProps}
                    weekly={vm?.attempts.weekly}
                    muted={!!vm?.attempts.tooFew}
                    testID="attempts-sparkline"
                    formatTooltipValue={(v) => formatAttemptsTooltipValue(
                      v ?? null,
                      t('admin.analytics.aiTutor.attempts.unit'),
                    )}
                  />
              )}
              />
              <HealthFigureDivider />
              <FigureBlock
                testID="score-gain-figure"
                label={t('admin.analytics.aiTutor.scoreGain.label')}
                value={formatScoreGainFigureValue(vm?.scoreGain.value ?? null)}
                animateNumeric={vm?.scoreGain.value ?? null}
                formatAnimated={formatScoreGainFigureValue}
                reduceMotion={reduceMotion}
                staggerIndex={1}
                unit={SCORE_GAIN_FIGURE_UNIT}
                caption={t('admin.analytics.aiTutor.scoreGain.caption')}
                tooFew={!!vm?.scoreGain.tooFew}
                dimmed={figuresDimmed}
                sparkline={(
                  <Sparkline
                    {...sparklineProps}
                    weekly={vm?.scoreGain.weekly}
                    muted={!!vm?.scoreGain.tooFew}
                    testID="score-gain-sparkline"
                    formatTooltipValue={(v) => formatScoreGainPp(v ?? null)}
                  />
              )}
              />
              <HealthFigureDivider />
              <FigureBlock
                testID="helpful-share-figure"
                label={t('admin.analytics.aiTutor.helpfulShare.label')}
                value={formatPercentFigureValue(vm?.helpfulShare.value ?? null)}
                animateNumeric={vm?.helpfulShare.value ?? null}
                formatAnimated={formatPercentFigureValue}
                reduceMotion={reduceMotion}
                staggerIndex={2}
                unit={PERCENT_FIGURE_UNIT}
                caption={t('admin.analytics.aiTutor.helpfulShare.caption', {
                  voteRate: voteRateDisplay,
                })}
                tooFew={!!vm?.helpfulShare.tooFew}
                dimmed={figuresDimmed}
                sparkline={(
                  <Sparkline
                    {...sparklineProps}
                    weekly={vm?.helpfulShare.weekly}
                    muted={!!vm?.helpfulShare.tooFew}
                    testID="helpful-share-sparkline"
                    formatTooltipValue={(v) => formatPercentValue(v ?? null)}
                  />
              )}
              />
              <HealthFigureDivider />
              <FigureBlock
                testID="paste-heavy-share-figure"
                label={t('admin.analytics.aiTutor.pasteHeavyShare.label')}
                value={formatPercentFigureValue(vm?.pasteHeavyShare.value ?? null)}
                animateNumeric={vm?.pasteHeavyShare.value ?? null}
                formatAnimated={formatPercentFigureValue}
                reduceMotion={reduceMotion}
                staggerIndex={3}
                unit={PERCENT_FIGURE_UNIT}
                caption={t('admin.analytics.aiTutor.pasteHeavyShare.caption')}
                tooFew={!!vm?.pasteHeavyShare.tooFew}
                dimmed={figuresDimmed}
                sparkline={(
                  <Sparkline
                    {...sparklineProps}
                    weekly={vm?.pasteHeavyShare.weekly}
                    muted={!!vm?.pasteHeavyShare.tooFew}
                    testID="paste-heavy-sparkline"
                    formatTooltipValue={(v) => formatPercentValue(v ?? null)}
                  />
              )}
              />
              <HealthFigureDivider />
              <FigureBlock
                testID="stuck-students-figure"
                label={t('admin.analytics.aiTutor.stuckStudents.label')}
                value={formatCountValue(vm?.stuckStudents.value ?? null)}
                animateNumeric={vm?.stuckStudents.value ?? null}
                formatAnimated={(n) => formatCountValue(n)}
                reduceMotion={reduceMotion}
                staggerIndex={4}
                caption={t('admin.analytics.aiTutor.stuckStudents.caption')}
                tooFew={!!vm?.stuckStudents.tooFew}
                dimmed={figuresDimmed}
              />
            </HStack>
          </Box>
        </DashboardSection>
        <DashboardSection
          testID="score-progression-section"
          sectionIndex={1}
          reduceMotion={reduceMotion}
          title={t('admin.analytics.scoreProgression.heading')}
          dimmed={figuresDimmed}
          headerRight={(
            <XAxisToggle
              value={scoreAxisMode}
              onChange={setScoreAxisMode}
            />
          )}
        >
          <TrendChartLegend />
          <ChartEntranceGate
            replayToken={chartReplayToken}
            minHeight={300}
            testID="score-chart-entrance"
          >
            {({ chartMountKey, entranceUnlocked }) => (
              entranceUnlocked ? (
                <TrendChart
                  testID="score-progression-chart"
                  chartMountKey={chartMountKey}
                  chartAnimationActive={chartAnimationActive}
                  points={scoreProgressionPoints}
                  scoreThreshold={viewModel?.scoreThreshold ?? null}
                  theme={chartTheme}
                  formatX={formatScoreX}
                />
              ) : null
            )}
          </ChartEntranceGate>
        </DashboardSection>
        <DashboardSection
          testID="feedback-helpfulness-section"
          sectionIndex={2}
          reduceMotion={reduceMotion}
          title={t('admin.analytics.feedbackHelpfulness.heading')}
          dimmed={figuresDimmed}
        >
          <HelpfulnessChartLegend />
          <ChartEntranceGate
            replayToken={chartReplayToken}
            minHeight={280}
            testID="helpfulness-chart-entrance"
          >
            {({ chartMountKey, entranceUnlocked }) => (
              entranceUnlocked ? (
                <StackedBarChart
                  testID="feedback-helpfulness-chart"
                  chartMountKey={chartMountKey}
                  chartAnimationActive={chartAnimationActive}
                  helpfulVotes={vm?.helpfulVotesWeek}
                  voteRateWeek={vm?.voteRateWeek}
                  releaseLines={releaseLines}
                  theme={chartTheme}
                  formatWeek={formatChartWeekAxis}
                />
              ) : null
            )}
          </ChartEntranceGate>
          <ReasonList reasons={vm?.thumbsDownReasons} />
        </DashboardSection>
      </SparklineTooltipLayerContext.Provider>
    </DashboardPage>
  );
};

export default AiTutorDashboard;
